import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, KeyRound, Mail, Phone, User, HelpCircle, ShieldQuestion } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginHelp = () => {
  const navigate = useNavigate();
  const [contact, setContact] = useState({ admins: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/auth/company-contact`)
      .then(res => setContact(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const hasContact = contact.admins && contact.admins.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0776BD] via-[#055A92] to-[#044A7A] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/mr-electric-logo.jpg" alt="Mr. Electric" className="h-24 w-auto object-contain rounded-lg bg-white p-2 shadow-lg" />
          </div>
          <p className="text-white/70 mt-1">Inventory Control</p>
        </div>

        <Card className="border-0 shadow-xl rounded-xl">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2">
              <HelpCircle size={22} className="text-[#0776BD]" />
              <CardTitle className="font-heading text-2xl tracking-tight text-[#1F2937]">Login Help</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Forgot Password */}
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-[#0776BD] hover:bg-[#E6F2FA]/50 transition-all text-left group"
              data-testid="help-forgot-password"
            >
              <div className="p-2.5 bg-[#E6F2FA] rounded-lg group-hover:bg-[#0776BD]/20 transition-colors">
                <KeyRound size={22} className="text-[#0776BD]" />
              </div>
              <div>
                <p className="font-medium text-[#1F2937]">I forgot my password</p>
                <p className="text-sm text-[#6B7280]">Get a reset link sent to your email</p>
              </div>
            </button>

            {/* Don't Know Email */}
            <div className="p-4 rounded-lg border border-gray-200 bg-[#FAFAFA]">
              <div className="flex items-start gap-4">
                <div className="p-2.5 bg-amber-50 rounded-lg">
                  <ShieldQuestion size={22} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-[#1F2937]">I don't know my login email</p>
                  <p className="text-sm text-[#6B7280] mt-1">
                    Your login email was set by your administrator when your account was created. Please contact your administrator for assistance.
                  </p>
                </div>
              </div>
            </div>

            {/* Admin Contact Info */}
            {!loading && hasContact && (
              <div className="p-4 rounded-lg border border-[#0776BD]/20 bg-[#E6F2FA]/30" data-testid="admin-contact-info">
                <p className="text-sm font-medium text-[#1F2937] mb-3">Administrator Contact</p>
                <div className="space-y-3">
                  {contact.admins.map((admin, idx) => (
                    <div key={admin.email || `admin-${idx}`} className="flex items-start gap-3 text-sm" data-testid={`admin-contact-${idx}`}>
                      <div className="p-1.5 bg-[#0776BD]/10 rounded-full mt-0.5">
                        <User size={14} className="text-[#0776BD]" />
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-[#1F2937] font-medium block">{admin.name}</span>
                        {admin.email && (
                          <a href={`mailto:${admin.email}`} className="flex items-center gap-1.5 text-[#0776BD] hover:underline text-xs">
                            <Mail size={12} /> {admin.email}
                          </a>
                        )}
                        {admin.phone_number && (
                          <a href={`tel:${admin.phone_number}`} className="flex items-center gap-1.5 text-[#0776BD] hover:underline text-xs">
                            <Phone size={12} /> {admin.phone_number}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!loading && !hasContact && (
              <div className="p-4 rounded-lg border border-gray-200 bg-[#FAFAFA]" data-testid="no-admin-contact">
                <p className="text-sm text-[#6B7280] text-center">
                  No administrator contact available. Please reach out to your organization directly.
                </p>
              </div>
            )}

            <div className="pt-2">
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full gap-2 rounded-lg border-gray-300 text-[#6B7280] hover:text-[#0776BD] hover:border-[#0776BD]"
                data-testid="back-to-login-from-help"
              >
                <ArrowLeft size={16} /> Back to Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginHelp;
